'use strict'

import sjcl from 'sjcl'
import { $q, $log } from '../helpers/angular-migration-helpers'
import FeatureCheck from '../helpers/featurecheck'
import { createEncryptionWorker } from '../helpers/worker'

# Initialize the SJCL entropy collector if no Crypto library is available
sjcl.random.startCollectors() unless sjcl.random.isReady()

class Encryption
  @aesSize = 256

  # TODO: We should change this with keep catching mouse movements, until
  # we have enough entropy to generate a random number without the 'random'
  # function.
  @seededRandomWords = (size) ->
    if not sjcl.random.isReady()
      # Keep adding 'random' numbers until we have enough
      while not sjcl.random.isReady()
        sjcl.random.addEntropy Math.random(), 1
      sjcl.random.stopCollectors()

    # Return the random words
    sjcl.random.randomWords size

  constructor: (aesPassPhrase) ->
    @aesPassPhrase = aesPassPhrase

  initializePromise: ->
    dfd = $q.defer()
    worker = createEncryptionWorker()
    # Assign web worker
    @webWorker = worker
    dfd.resolve()

    # Return the promise
    dfd.promise

  getString: ->
    "encryption:passphrase='#{@aesPassPhrase}'"

  @getRandomPassword: (maxLength) ->
    # Get enough random data for a password
    randomWords = @seededRandomWords Encryption.aesSize / 32

    # Convert it back to ASCII (base-64)
    text = sjcl.codec.base64.fromBits randomWords

    # If a maximum length has been specified, then truncate it
    if maxLength and text.length > maxLength
      text = text.substring 0, maxLength

    # Strip the padding '='
    while text[text.length - 1] == '='
      text = text.substring 0, text.length - 1

    # Return text
    text

  processFile: (fileInfo) ->
    # If encryption isn't supported, then we won't compress
    return true if !FeatureCheck.canEncrypt()

    # All files (with contents) will be encrypted by default if this plug-in is enabled
    # (it's important to mark files at this point, because compression adapts to encrypted transfers)
    if @aesPassPhrase and fileInfo.size > 0
      # Mark file as encrypted
      fileInfo.isEncrypted = true

      # Generate a salt for encrypted files
      fileInfo.aesRandom = Encryption.seededRandomWords Encryption.aesSize / 64

  processSlice: (fileInfo, unencryptedBlob, chunk) ->
    # Check if we need to encrypt the slice
    return unencryptedBlob if !fileInfo.isEncrypted

    # Determine when we started processing
    start = Date.now()

    # Encrypt the blob
    dfd = $q.defer()

    # Add the response handler
    responseHandler = (e) =>
      if e.data.id == fileInfo.id
        # Handle errors
        if e.data.chunk isnt chunk
          $log.error(
            "#{fileInfo.name} (#{chunk + 1}/#{
              fileInfo.totalChunks
            }): Encryption expected chunk #{chunk}, but got #{e.data.chunk}."
          )

        # Create the complete blob
        encryptedBlob = new Blob e.data.encryptedBlocks

        # Update fileInfo with salt, pwver and hash
        fileInfo.aesSalt = e.data.aesSalt if e.data.aesSalt
        fileInfo.aesPwVer = e.data.aesPwVer if e.data.aesPwVer
        fileInfo.aesHash = e.data.aesHash if e.data.aesHash

        # Release the event listener again
        @webWorker.removeEventListener 'message', responseHandler, false

        # Determine when we finished constructing the new blob
        end = Date.now()

        # Log
        $log.log(
          "#{fileInfo.name} (#{chunk + 1}/#{
            fileInfo.totalChunks
          }): Encryption (#{end - start}ms, original #{
            unencryptedBlob.size
          }, encrypted #{encryptedBlob.size})"
        )

        # Resolve the operation with the compressed blob
        dfd.resolve encryptedBlob

    @webWorker.addEventListener 'message', responseHandler

    # Load the file
    fr = new FileReader()

    # Error handlers
    fr.onabort = fr.onerror = ->
      $log.error(
        "Unable to read file '#{fileInfo.name}': #{fr.error.message} (#{
          fr.error.name
        })"
      )
      dfd.reject()

    # Success flow
    fr.onload = =>
      # Post message, so the worker can start processing
      sendData =
        id: fileInfo.id
        fileName: fileInfo.name
        chunk: chunk
        totalChunks: fileInfo.totalChunks
        unencryptedData: fr.result
        aesSize: Encryption.aesSize
        aesPassPhrase: @aesPassPhrase
        aesRandom: fileInfo.aesRandom
      @webWorker.postMessage sendData, [sendData.unencryptedData]

    # Read the data and return
    fr.readAsArrayBuffer unencryptedBlob
    dfd.promise

export default Encryption
